'use client'

import React, { memo, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import Avatar, { NoAssigneeAvatar } from '@/components/common/Avatar'
import { Button } from '@/components/common/ComboboxV2/Button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2/Combobox'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { User } from '@/types/User'

const NO_ASSIGNEE_HASH = 'no-assignee'

type Align = 'start' | 'center' | 'end'
type Side = 'top' | 'bottom' | 'left' | 'right'

interface AssigneeComboboxProps {
  align?: Align
  side?: Side
  triggerAs?: React.ElementType
  children?: React.ReactNode
  triggerClassName?: string
  value: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  // narrows the selectable users (the current assignee still renders in the trigger)
  filterUsers?: (user: User) => boolean
}

interface AssigneeComboboxMultiProps {
  align?: Align
  side?: Side
  triggerAs?: React.ElementType
  children?: React.ReactNode
  triggerClassName?: string
  value: string[]
  onChange: (value: string[]) => void
}

interface AssigneeComboboxContentProps {
  align?: Align
  alignOffset?: number
  side?: Side
  sideOffset?: number
  className?: string
  portal?: boolean
  multiple?: boolean
  includeNoAssignee?: boolean
  value: string[] | null
  onChange: (value: string[] | null) => void
  filterUsers?: (user: User) => boolean
}

const hashUser = (user: User) => {
  // needs to be searchable
  return `${user.first_name} ${user.last_name} ${user.email}`
}

export const AssigneeComboboxContent: React.FC<
  AssigneeComboboxContentProps
> = ({
  align = 'start',
  side = 'bottom',
  alignOffset,
  sideOffset,
  className,
  multiple = false,
  includeNoAssignee = true,
  portal,
  value,
  onChange,
  filterUsers,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: organizationUsers, isLoading } = useOrganizationUsers({
    orgId: activeOrgId,
  })

  const sortedUsers = useMemo(() => {
    const users = filterUsers
      ? organizationUsers?.filter(filterUsers)
      : organizationUsers
    return users?.sort((a, b) => a.first_name.localeCompare(b.first_name))
  }, [organizationUsers, filterUsers])

  const handleSelectNoAssignee = () => {
    onChange(null)
  }

  return (
    <ComboboxContent
      className={twMerge('p-0 sm:max-w-[212px]', className)}
      side={side}
      align={align}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
      portal={portal}
    >
      <ComboboxInput
        placeholder="Assign to..."
        className="placeholder:text-[13px]"
      />
      <ComboboxList>
        <ComboboxEmpty className="text-[13px]">
          {isLoading ? 'Loading...' : 'No results found.'}
        </ComboboxEmpty>
        {sortedUsers && (
          <ComboboxGroup>
            {sortedUsers.map((user) => {
              const isSelected = value?.includes(user.id) ?? false
              const hash = hashUser(user)
              return (
                <ComboboxSelectableItem
                  key={hash}
                  value={hash}
                  isSelected={isSelected}
                  onSelect={() => {
                    if (!multiple) {
                      onChange([user.id])
                      return
                    }
                    const newValue = value ?? []
                    if (isSelected) {
                      onChange(newValue.filter((id) => id !== user.id))
                    } else {
                      onChange([...newValue, user.id])
                    }
                  }}
                >
                  <div className="flex items-center gap-x-2 max-w-48">
                    <Avatar
                      src={user.picture_url}
                      width={22}
                      height={22}
                      name={`${user.first_name} ${user.last_name}`}
                      noTooltip={true}
                      labelClassName="text-[9px]"
                    />
                    <span className="truncate">{`${user.first_name} ${user.last_name}`}</span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
            {includeNoAssignee && (
              <ComboboxSelectableItem
                key={NO_ASSIGNEE_HASH}
                value={NO_ASSIGNEE_HASH}
                isSelected={value === null || value.length === 0}
                onSelect={handleSelectNoAssignee}
              >
                <div className="flex items-center gap-x-2">
                  <div className="flex items-center justify-center">
                    <NoAssigneeAvatar width={22} height={22} />
                  </div>
                  <span>No assignee</span>
                </div>
              </ComboboxSelectableItem>
            )}
          </ComboboxGroup>
        )}
      </ComboboxList>
    </ComboboxContent>
  )
}

export const AssigneeCombobox: React.FC<AssigneeComboboxProps> = ({
  align = 'start',
  side = 'bottom',
  triggerAs: TriggerAs,
  children,
  triggerClassName,
  value,
  onChange,
  disabled = false,
  filterUsers,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: organizationUsers } = useOrganizationUsers({
    orgId: activeOrgId,
  })

  const [open, setOpen] = React.useState(false)

  const selectedUser = organizationUsers?.find((user) => user.id === value)
  const selectedUserFullName = `${selectedUser?.first_name} ${selectedUser?.last_name}`

  const handleChange = (value: string[] | null) => {
    if (value === null || value.length === 0 || value[0] === NO_ASSIGNEE_HASH) {
      onChange(null)
    } else {
      const user = organizationUsers?.find((user) => user.id === value[0])
      if (user) {
        onChange(user.id)
      }
    }
    setOpen(false)
  }

  const buttonChildren = (
    <>
      {selectedUser ? (
        <>
          <Avatar
            src={selectedUser.picture_url}
            width={18}
            height={18}
            name={selectedUserFullName}
            noTooltip={true}
            labelClassName="text-[9px]"
          />
          <span className="text-[13px]">{selectedUserFullName}</span>
        </>
      ) : (
        <>
          <NoAssigneeAvatar width={18} height={18} />
          <span className="text-[13px]">No assignee</span>
        </>
      )}
    </>
  )
  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild={TriggerAs ? false : true} disabled={disabled}>
        {children ? (
          children
        ) : TriggerAs ? (
          <TriggerAs className={triggerClassName}>{buttonChildren}</TriggerAs>
        ) : (
          <Button className={triggerClassName}>{buttonChildren}</Button>
        )}
      </ComboboxTrigger>
      {/*
        not sure why, but when using this component inside a Dialog,
        the Dialog would double render which would cause it to mount twice
        and thus do its animation twice; this is a workaround to delay
        mounting until actually needed;
        TODO: figure out why this is happening and fix it
        */}
      {open && (
        <AssigneeComboboxContent
          align={align}
          side={side}
          value={value ? [value] : null}
          onChange={handleChange}
          filterUsers={filterUsers}
        />
      )}
    </Combobox>
  )
}

export const AssigneeComboboxMulti: React.FC<AssigneeComboboxMultiProps> = ({
  align = 'start',
  side = 'bottom',
  triggerAs: TriggerAs,
  children,
  triggerClassName,
  value,
  onChange,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: organizationUsers } = useOrganizationUsers({
    orgId: activeOrgId,
  })

  const [open, setOpen] = React.useState(false)

  const selectedUsers = useMemo(
    () => organizationUsers?.filter((user) => value?.includes(user.id)),
    [organizationUsers, value],
  )

  const handleChange = (value: string[] | null) => {
    if (value === null || value.length === 0 || value[0] === NO_ASSIGNEE_HASH) {
      onChange([])
    } else {
      onChange(value)
    }
  }

  const buttonChildren = (
    <>
      {selectedUsers && selectedUsers.length > 0 ? (
        <div className="flex items-center gap-1">
          <div className="flex items-center -space-x-[2px]">
            {selectedUsers.map((user) => (
              <div
                key={user.id}
                className="rounded-full ring-[1.5px] ring-white dark:ring-primary-900"
              >
                <Avatar
                  src={user.picture_url}
                  width={15}
                  height={15}
                  name={`${user.first_name} ${user.last_name}`}
                  noTooltip={true}
                  labelClassName="text-[7px]"
                />
              </div>
            ))}
          </div>
          <span>
            {selectedUsers.length > 1
              ? `${selectedUsers.length} assignees`
              : `${selectedUsers[0].first_name} ${selectedUsers[0].last_name}`}
          </span>
        </div>
      ) : (
        <>
          <NoAssigneeAvatar width={18} height={18} />
          <span>No assignee</span>
        </>
      )}
    </>
  )

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild={TriggerAs ? false : true}>
        {children ? (
          children
        ) : TriggerAs ? (
          <TriggerAs className={twMerge('text-[13px]', triggerClassName)}>
            {buttonChildren}
          </TriggerAs>
        ) : (
          <Button className={twMerge('text-[13px]', triggerClassName)}>
            {buttonChildren}
          </Button>
        )}
      </ComboboxTrigger>
      {/*
        not sure why, but when using this component inside a Dialog,
        the Dialog would double render which would cause it to mount twice
        and thus do its animation twice; this is a workaround to delay
        mounting until actually needed;
        TODO: figure out why this is happening and fix it
        */}
      {open && (
        <AssigneeComboboxContent
          align={align}
          side={side}
          value={value}
          multiple={true}
          onChange={handleChange}
        />
      )}
    </Combobox>
  )
}

AssigneeCombobox.displayName = 'AssigneeCombobox'
AssigneeComboboxMulti.displayName = 'AssigneeComboboxMulti'
